import Papa from "papaparse";
import { type FC, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useDropzone } from "react-dropzone";
import { MetaAdsEngagementSource, MetaAdsWebsiteAudienceRule } from "shared";
import { Select } from "../../components/Select";
import { useAudienceActions } from "../useAudiences";
import { hashAudienceData } from "./hashUtil";

const COLUMN_OPTIONS = [
	{ value: "SKIP", label: "Skip" },
	{ value: "EMAIL", label: "Email" },
	{ value: "PHONE", label: "Phone" },
	{ value: "FN", label: "First Name" },
	{ value: "LN", label: "Last Name" },
	{ value: "ZIP", label: "Zip Code" },
];

type Tab = "list" | "website" | "engagement";

export const ModalCreateAudience: FC<{ onClose: () => void }> = ({
	onClose,
}) => {
	const [tab, setTab] = useState<Tab>("list");

	return createPortal(
		<div
			className="modal d-block"
			role="dialog"
			style={{ background: "rgba(0,0,0,0.5)" }}
			onClick={(e) => e.target === e.currentTarget && onClose()}
			onKeyDown={(e) => e.key === "Escape" && onClose()}
		>
			<div className="modal-dialog modal-lg modal-dialog-centered">
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">Create Custom Audience</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
							aria-label="Close"
						/>
					</div>
					<div className="modal-body">
						<ul className="nav nav-tabs mb-3">
							{(
								[
									{ key: "list", label: "Customer List" },
									{ key: "website", label: "Website Traffic" },
									{ key: "engagement", label: "Engagement" },
								] as const
							).map((t) => (
								<li className="nav-item" key={t.key}>
									<button
										type="button"
										className={`nav-link ${tab === t.key ? "active" : ""}`}
										onClick={() => setTab(t.key)}
									>
										{t.label}
									</button>
								</li>
							))}
						</ul>

						{tab === "list" && <CustomerListTab onClose={onClose} />}
						{tab === "website" && <WebsiteTrafficTab onClose={onClose} />}
						{tab === "engagement" && <EngagementTab onClose={onClose} />}
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
};

const CustomerListTab: FC<{ onClose: () => void }> = ({ onClose }) => {
	const { createCustomList } = useAudienceActions();
	const [name, setName] = useState("");
	const [csvRows, setCsvRows] = useState<string[][]>([]);
	const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
	const [columnMapping, setColumnMapping] = useState<string[]>([]);
	const [hashing, setHashing] = useState(false);
	const [fileName, setFileName] = useState("");

	const onDrop = useCallback((files: File[]) => {
		const file = files[0];
		if (!file) return;
		setFileName(file.name);
		Papa.parse(file, {
			complete: (result) => {
				const data = result.data as string[][];
				if (data.length > 0) {
					setCsvHeaders(data[0]);
					setCsvRows(
						data.slice(1).filter((row) => row.some((cell) => cell.trim())),
					);
					setColumnMapping(data[0].map(() => "SKIP"));
				}
			},
			skipEmptyLines: true,
		});
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: { "text/csv": [".csv"] },
		maxFiles: 1,
	});

	const handleSubmit = async () => {
		if (!name.trim() || csvRows.length === 0) return;
		const hasMapping = columnMapping.some((c) => c !== "SKIP");
		if (!hasMapping) return;

		setHashing(true);
		try {
			const { schema, hashedData } = await hashAudienceData(
				csvRows,
				columnMapping,
			);
			await createCustomList.mutateAsync({
				name,
				columnMapping: schema,
				hashedData,
			});
			onClose();
		} finally {
			setHashing(false);
		}
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<div>
				<label className="form-label" htmlFor="audience-name-list">
					Audience Name
				</label>
				<input
					id="audience-name-list"
					type="text"
					className="form-control"
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="e.g. Newsletter Subscribers"
				/>
			</div>

			<div
				{...getRootProps()}
				style={{
					border: "2px dashed #dee2e6",
					borderRadius: 8,
					padding: 24,
					textAlign: "center",
					cursor: "pointer",
					background: isDragActive ? "#f0f7ff" : undefined,
				}}
			>
				<input {...getInputProps()} />
				{fileName ? (
					<p style={{ marginBottom: 0 }}>
						<strong>{fileName}</strong> - {csvRows.length} rows loaded
					</p>
				) : (
					<p style={{ marginBottom: 0 }}>
						Drop a CSV file here, or click to select
					</p>
				)}
			</div>

			{csvHeaders.length > 0 && (
				<div>
					<h6>Column Mapping</h6>
					<small className="text-muted d-block mb-2">
						Map each CSV column to a data type. Data is hashed (SHA-256) before
						upload — raw data never leaves your browser.
					</small>
					<table className="table table-sm">
						<thead>
							<tr>
								<th>CSV Column</th>
								<th>Map To</th>
								<th>Preview</th>
							</tr>
						</thead>
						<tbody>
							{csvHeaders.map((header, i) => (
								<tr key={header}>
									<td>{header}</td>
									<td>
										<Select
											className="form-select form-select-sm"
											value={columnMapping[i] ?? "SKIP"}
											onChange={(e) => {
												const updated = [...columnMapping];
												updated[i] = e.target.value;
												setColumnMapping(updated);
											}}
										>
											{COLUMN_OPTIONS.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</Select>
									</td>
									<td className="text-muted">{csvRows[0]?.[i] ?? "-"}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
				<button
					type="button"
					className="btn btn-outline-secondary"
					onClick={onClose}
				>
					Cancel
				</button>
				<button
					type="button"
					className="btn btn-primary"
					disabled={
						!name.trim() ||
						csvRows.length === 0 ||
						!columnMapping.some((c) => c !== "SKIP") ||
						hashing ||
						createCustomList.isPending
					}
					onClick={handleSubmit}
				>
					{hashing
						? "Hashing data..."
						: createCustomList.isPending
							? "Creating..."
							: "Create Audience"}
				</button>
			</div>
		</div>
	);
};

const WebsiteTrafficTab: FC<{ onClose: () => void }> = ({ onClose }) => {
	const { createWebsite } = useAudienceActions();
	const [name, setName] = useState("");
	const [pixelId, setPixelId] = useState("");
	const [rule, setRule] = useState<MetaAdsWebsiteAudienceRule>(
		MetaAdsWebsiteAudienceRule.ALL_VISITORS,
	);
	const [retentionDays, setRetentionDays] = useState(30);
	const [urls, setUrls] = useState("");
	const [eventName, setEventName] = useState("");

	const handleSubmit = async () => {
		if (!name.trim() || !pixelId.trim()) return;
		await createWebsite.mutateAsync({
			name,
			pixelId,
			rule,
			retentionDays,
			urls:
				rule === MetaAdsWebsiteAudienceRule.SPECIFIC_URLS
					? urls
							.split("\n")
							.map((u) => u.trim())
							.filter(Boolean)
					: undefined,
			eventName:
				rule === MetaAdsWebsiteAudienceRule.CUSTOM_EVENT
					? eventName
					: undefined,
		});
		onClose();
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<div>
				<label className="form-label" htmlFor="audience-name-web">
					Audience Name
				</label>
				<input
					id="audience-name-web"
					type="text"
					className="form-control"
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="e.g. All Website Visitors (30 days)"
				/>
			</div>
			<div>
				<label className="form-label" htmlFor="pixel-id-web">
					Meta Pixel ID
				</label>
				<input
					id="pixel-id-web"
					type="text"
					className="form-control"
					value={pixelId}
					onChange={(e) => setPixelId(e.target.value)}
					placeholder="Enter your Meta Pixel ID"
				/>
			</div>
			<div>
				<label className="form-label" htmlFor="rule-web">
					Rule
				</label>
				<Select
					id="rule-web"
					className="form-select"
					value={rule}
					onChange={(e) =>
						setRule(e.target.value as MetaAdsWebsiteAudienceRule)
					}
				>
					<option value={MetaAdsWebsiteAudienceRule.ALL_VISITORS}>
						All Visitors
					</option>
					<option value={MetaAdsWebsiteAudienceRule.SPECIFIC_URLS}>
						Specific URLs
					</option>
					<option value={MetaAdsWebsiteAudienceRule.TIME_SPENT}>
						Time Spent (Top 25%)
					</option>
					<option value={MetaAdsWebsiteAudienceRule.CUSTOM_EVENT}>
						Custom Event
					</option>
				</Select>
			</div>
			{rule === MetaAdsWebsiteAudienceRule.SPECIFIC_URLS && (
				<div>
					<label className="form-label" htmlFor="urls-web">
						URLs (one per line)
					</label>
					<textarea
						id="urls-web"
						className="form-control"
						rows={3}
						value={urls}
						onChange={(e) => setUrls(e.target.value)}
						placeholder="/pricing&#10;/products"
					/>
				</div>
			)}
			{rule === MetaAdsWebsiteAudienceRule.CUSTOM_EVENT && (
				<div>
					<label className="form-label" htmlFor="event-web">
						Event Name
					</label>
					<input
						id="event-web"
						type="text"
						className="form-control"
						value={eventName}
						onChange={(e) => setEventName(e.target.value)}
						placeholder="e.g. Purchase"
					/>
				</div>
			)}
			<div>
				<label className="form-label" htmlFor="retention-web">
					Retention Days (1-180)
				</label>
				<input
					id="retention-web"
					type="number"
					className="form-control"
					min={1}
					max={180}
					value={retentionDays}
					onChange={(e) => setRetentionDays(Number(e.target.value))}
				/>
			</div>
			<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
				<button
					type="button"
					className="btn btn-outline-secondary"
					onClick={onClose}
				>
					Cancel
				</button>
				<button
					type="button"
					className="btn btn-primary"
					disabled={!name.trim() || !pixelId.trim() || createWebsite.isPending}
					onClick={handleSubmit}
				>
					{createWebsite.isPending ? "Creating..." : "Create Audience"}
				</button>
			</div>
		</div>
	);
};

const EngagementTab: FC<{ onClose: () => void }> = ({ onClose }) => {
	const { createEngagement } = useAudienceActions();
	const [name, setName] = useState("");
	const [source, setSource] = useState<MetaAdsEngagementSource>(
		MetaAdsEngagementSource.FB_PAGE,
	);
	const [retentionDays, setRetentionDays] = useState(30);
	const [engagementType, setEngagementType] = useState("");

	const handleSubmit = async () => {
		if (!name.trim()) return;
		await createEngagement.mutateAsync({
			name,
			source,
			retentionDays,
			engagementType: engagementType || undefined,
		});
		onClose();
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<div>
				<label className="form-label" htmlFor="audience-name-eng">
					Audience Name
				</label>
				<input
					id="audience-name-eng"
					type="text"
					className="form-control"
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="e.g. FB Page Engagers (90 days)"
				/>
			</div>
			<div>
				<label className="form-label" htmlFor="source-eng">
					Engagement Source
				</label>
				<Select
					id="source-eng"
					className="form-select"
					value={source}
					onChange={(e) => setSource(e.target.value as MetaAdsEngagementSource)}
				>
					<option value={MetaAdsEngagementSource.FB_PAGE}>Facebook Page</option>
					<option value={MetaAdsEngagementSource.IG_PROFILE}>
						Instagram Profile
					</option>
					<option value={MetaAdsEngagementSource.LEAD_FORM}>Lead Form</option>
					<option value={MetaAdsEngagementSource.VIDEO}>Video</option>
					<option value={MetaAdsEngagementSource.EVENTS}>Events</option>
				</Select>
			</div>
			<div>
				<label className="form-label" htmlFor="eng-type">
					Engagement Type (optional)
				</label>
				<input
					id="eng-type"
					type="text"
					className="form-control"
					value={engagementType}
					onChange={(e) => setEngagementType(e.target.value)}
					placeholder="e.g. People who liked your page"
				/>
			</div>
			<div>
				<label className="form-label" htmlFor="retention-eng">
					Retention Days (1-365)
				</label>
				<input
					id="retention-eng"
					type="number"
					className="form-control"
					min={1}
					max={365}
					value={retentionDays}
					onChange={(e) => setRetentionDays(Number(e.target.value))}
				/>
			</div>
			<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
				<button
					type="button"
					className="btn btn-outline-secondary"
					onClick={onClose}
				>
					Cancel
				</button>
				<button
					type="button"
					className="btn btn-primary"
					disabled={!name.trim() || createEngagement.isPending}
					onClick={handleSubmit}
				>
					{createEngagement.isPending ? "Creating..." : "Create Audience"}
				</button>
			</div>
		</div>
	);
};
